import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Progress, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type Var = { name: string; value: string };
type Current = {
  repository: string;
  regions: Record<string, { exists: boolean; envVars: Var[]; minReplicas?: number; maxReplicas?: number }>;
  shared: Var[];
};
type Row = Var & { current: string; remove: boolean };
type Edit = { rows: Row[]; added: Var[]; min: string; max: string };
export type RegionChanges = { envVars: Var[]; removeEnvVars: string[]; minReplicas?: number; maxReplicas?: number };

const NAME = /^[A-Z][A-Z0-9_]*$/;

const fromCurrent = (r?: Current['regions'][string]): Edit => ({
  rows: (r?.envVars ?? []).map(v => ({ ...v, current: v.value, remove: false })),
  added: [],
  min: r?.minReplicas === undefined ? '' : String(r.minReplicas),
  max: r?.maxReplicas === undefined ? '' : String(r.maxReplicas),
});

/** Solo lo que cambió respecto a lo actual. */
export const diff = (edit: Edit, current?: Current['regions'][string]): RegionChanges => {
  const out: RegionChanges = {
    envVars: [
      ...edit.rows.filter(r => !r.remove && r.value !== r.current),
      ...edit.added.filter(v => v.name),
    ].map(({ name, value }) => ({ name, value })),
    removeEnvVars: edit.rows.filter(r => r.remove).map(r => r.name),
  };
  if (edit.min !== '' && Number(edit.min) !== current?.minReplicas) out.minReplicas = Number(edit.min);
  if (edit.max !== '' && Number(edit.max) !== current?.maxReplicas) out.maxReplicas = Number(edit.max);
  return out;
};

const isEmpty = (c: RegionChanges) =>
  !c.envVars.length && !c.removeEnvVars.length && c.minReplicas === undefined && c.maxReplicas === undefined;

/**
 * Editor de variables y réplicas: muestra los valores actuales del servicio (paso anterior: microservicio, ambiente
 * y regiones) para cambiarlos en el sitio, añadir variables o quitarlas. Devuelve solo los cambios, por región.
 */
export const ArgoValuesEditor = ({ onChange, formContext, rawErrors }: FieldExtensionComponentProps<Record<string, RegionChanges>>) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const form = (formContext?.formData ?? {}) as Record<string, any>;
  const component = String(form.component ?? '').split('/').pop() ?? '';
  const environment = String(form.environment ?? '');
  const [current, setCurrent] = useState<Current>();
  // shared (servicios internos e IA): sin regiones; una sección por carpeta de despliegue (p. ej. ai-hub -api y -web).
  const regionsKey = environment === 'shared'
    ? Object.keys(current?.regions ?? {}).join(',')
    : ((form.regions?.length ? form.regions : ['r1', 'r2']) as string[]).join(',');
  const regions = useMemo<string[]>(() => regionsKey.split(',').filter(Boolean), [regionsKey]);
  const [error, setError] = useState<string>();
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [same, setSame] = useState(true);
  const [filter, setFilter] = useState('');
  // El formulario puede dar un onChange nuevo en cada render: se usa siempre el último sin re-ejecutar el efecto.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!component || !environment) return;
    setCurrent(undefined);
    setError(undefined);
    (async () => {
      const base = await discovery.getBaseUrl('argo-values');
      const res = await fetch(`${base}/current?component=${encodeURIComponent(component)}&environment=${environment}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      setCurrent(body);
      setEdits(Object.fromEntries(Object.entries(body.regions as Current['regions']).map(([r, v]) => [r, fromCurrent(v)])));
    })().catch(e => setError(String(e?.message ?? e)));
  }, [component, environment, discovery, fetch]);

  // Las carpetas de shared son despliegues distintos: nunca se copian los cambios de una a otra.
  const mirror = same && regions.length > 1 && environment !== 'shared';
  const editing = mirror ? [regions[0]] : regions;

  // Valores que difieren entre regiones (p. ej. AWS_REGION): se avisan al editar las dos a la vez.
  const differs = useMemo(() => {
    if (!current || !mirror) return [];
    const [a, b] = regions.map(r => new Map(current.regions[r]?.envVars.map(v => [v.name, v.value])));
    return [...a.keys()].filter(k => b.has(k) && a.get(k) !== b.get(k));
  }, [current, mirror, regions]);

  useEffect(() => {
    if (!current) return;
    const out: Record<string, RegionChanges> = {};
    // Mismos cambios en las dos regiones: lo que se tocó en la primera se aplica igual en la otra.
    for (const r of regions) {
      const source = mirror ? regions[0] : r;
      if (edits[source]) out[r] = diff(edits[source], current.regions[source]);
    }
    onChangeRef.current(out);
  }, [edits, mirror, current, regions]);

  if (!component || !environment) return <Typography color="textSecondary">Elige el servicio y el ambiente en el paso anterior.</Typography>;
  if (error) return <WarningPanel title={`No se pudieron leer los valores de ${component} en ${environment}`} message={error} />;
  if (!current) return <Progress />;

  const heading = (r: string) => {
    if (environment === 'shared') return `${r} (deployment.yaml)`;
    return mirror ? `Regiones ${regions.join(' y ')}` : `Región ${r}`;
  };
  const update = (r: string, change: (e: Edit) => Edit) => setEdits(prev => ({ ...prev, [r]: change(prev[r]) }));

  return (
    <div>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
        Valores actuales de <b>{component}</b> en <b>{environment}</b> ({current.repository}). Cambia lo que necesites: solo se
        envían los cambios. En dev/qa se aprueba solo si no quitas variables, LOG_LEVEL es debug/info/warn/error y las réplicas
        quedan entre 1 y 3; si no, lo aprueba devops. Prod y shared siempre los aprueba devops.
      </Typography>
      {regions.length > 1 && environment !== 'shared' && (
        <FormControlLabel
          control={<Checkbox checked={same} onChange={e => setSame(e.target.checked)} color="primary" />}
          label={`Mismos cambios en ${regions.join(' y ')}`}
        />
      )}
      {differs.length > 0 && (
        <Typography variant="caption" color="textSecondary" component="div">
          Distintas entre regiones (se muestran las de {regions[0]}; solo cambian si las editas): {differs.join(', ')}
        </Typography>
      )}
      <TextField size="small" label="Buscar variable" value={filter} onChange={e => setFilter(e.target.value.toUpperCase())} style={{ margin: '8px 0' }} />

      {editing.map(r => {
        const edit = edits[r];
        if (!edit) return null;
        const info = current.regions[r];
        return (
          <div key={r} style={{ marginBottom: 24 }}>
            <Typography variant="h6">
              {heading(r)}
              {!info?.exists && ' (sin archivos todavía: se crean)'}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Variable</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Quitar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {edit.rows
                  .map((row, i) => ({ row, i }))
                  .filter(({ row }) => row.name.includes(filter))
                  .map(({ row, i }) => (
                    <TableRow key={row.name} style={row.remove ? { opacity: 0.5 } : undefined}>
                      <TableCell style={{ fontFamily: 'monospace' }}>{row.name}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          disabled={row.remove}
                          value={row.value}
                          helperText={row.value !== row.current ? `Antes: ${row.current}` : undefined}
                          onChange={e => update(r, ed => ({ ...ed, rows: ed.rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) }))}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={row.remove}
                          onChange={e => update(r, ed => ({ ...ed, rows: ed.rows.map((x, j) => (j === i ? { ...x, remove: e.target.checked } : x)) }))}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                {edit.added.map((v, i) => (
                  <TableRow key={`nueva-${i}`}>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="NOMBRE_NUEVO"
                        value={v.name}
                        error={!!v.name && !NAME.test(v.name)}
                        helperText={v.name && !NAME.test(v.name) ? 'MAYÚSCULAS, números y _' : 'Nueva'}
                        onChange={e => update(r, ed => ({ ...ed, added: ed.added.map((x, j) => (j === i ? { ...x, name: e.target.value.toUpperCase() } : x)) }))}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={v.value}
                        onChange={e => update(r, ed => ({ ...ed, added: ed.added.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) }))}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => update(r, ed => ({ ...ed, added: ed.added.filter((_, j) => j !== i) }))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Button size="small" startIcon={<AddIcon />} onClick={() => update(r, ed => ({ ...ed, added: [...ed.added, { name: '', value: '' }] }))}>
              Añadir variable
            </Button>

            <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
              <TextField
                size="small"
                type="number"
                label="Réplicas mínimas"
                value={edit.min}
                inputProps={{ min: 1, max: 50 }}
                helperText={`Actual: ${info?.minReplicas ?? 'valor por defecto del chart'}`}
                onChange={e => update(r, ed => ({ ...ed, min: e.target.value }))}
              />
              <TextField
                size="small"
                type="number"
                label="Réplicas máximas"
                value={edit.max}
                inputProps={{ min: 1, max: 50 }}
                helperText={`Actual: ${info?.maxReplicas ?? 'valor por defecto del chart'}`}
                onChange={e => update(r, ed => ({ ...ed, max: e.target.value }))}
              />
            </div>
          </div>
        );
      })}

      {current.shared.length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Variables comunes a todos los ambientes ({current.shared.length}) · solo lectura</Typography>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }}>
            <Typography variant="caption" color="textSecondary">
              Viven en shared-envvars.yaml y también afectan a prod: para cambiarlas, pídeselo a devops.
            </Typography>
            {current.shared.map(v => (
              <div key={v.name} style={{ fontFamily: 'monospace', fontSize: 13 }}>
                {v.name} = {v.value}
              </div>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
      {rawErrors?.map(e => (
        <Typography key={e} color="error" variant="body2">
          {e}
        </Typography>
      ))}
    </div>
  );
};

/** Debe haber al menos un cambio y nombres válidos en las variables nuevas. */
export const validateArgoValues = (value: Record<string, RegionChanges> | undefined, validation: { addError: (m: string) => void }) => {
  const all = Object.values(value ?? {});
  if (!all.length || all.every(isEmpty)) validation.addError('No hay cambios: edita un valor, añade o quita una variable, o cambia las réplicas');
  for (const c of all) {
    for (const v of c.envVars) if (!NAME.test(v.name)) validation.addError(`Nombre inválido: ${v.name}`);
    if (c.minReplicas !== undefined && c.maxReplicas !== undefined && c.minReplicas > c.maxReplicas) validation.addError('El mínimo de réplicas no puede ser mayor que el máximo');
  }
};
